import { Stack, Box, Tooltip, Collapse, Typography } from "@mui/material";
import { ReactElement, cloneElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LeftNavItemInterface } from "../../interfaces";
import { leftNavItems, DISABLED_PAGES, DISABLED_ICON_TOOLTIP_MESSAGE } from "../../constants";
import { globalTheme } from "../../styles";
import { filterNavItemsByRole } from "../../apiHooks";

export const LeftNavMenuItems = ({ isLeftNavExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const filteredNavItems = filterNavItemsByRole(leftNavItems);

  // Find the matching leftNavItem based on the current route
  const registeredNavItem = filteredNavItems?.filter((item) => location.pathname === item.redirectTo);
  const [activeMenu, setActiveMenu] = useState<LeftNavItemInterface>(
    registeredNavItem
      ? registeredNavItem[0]
      : {
          redirectTo: location.pathname, // Stay on the manually entered location(url) instead of routing to error path or dashboard
        }
  );

  const [hoveredMenu, setHoveredMenu] = useState<number | null>(null);

  useEffect(() => {
    if (registeredNavItem) {
      setActiveMenu(registeredNavItem[0]);
    }
  }, [location]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Skip navigation on initial render
    timeoutId = setTimeout(() => {
      navigate(activeMenu.redirectTo);
    }, 50); // Adjust the delay as needed

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeMenu]);

  const handleLeftNavSelection = (key: number) => {
    setActiveMenu(() => filteredNavItems[key]);
  };

  const handleOnClickSubmenuItem = (subItem, menuItem) => {
    localStorage.setItem(menuItem.localStorageSetSubMenu, subItem.tabId);
    navigate(menuItem.redirectTo);
    window.location.reload();
  };

  return (
    <Stack spacing={2}>
      {filteredNavItems?.map((menuItem, key) => {
        const isDisabledPage = DISABLED_PAGES.includes(menuItem?.key);
        const tooltipTitle = isDisabledPage ? DISABLED_ICON_TOOLTIP_MESSAGE : menuItem.title;

        const handleMouseEnter = (key: number) => {
          // Set the hovered menu item index
          setHoveredMenu(key);
        };

        const handleMouseLeave = () => {
          // Reset the hovered menu when mouse leaves
          setHoveredMenu(null);
        };

        const menuItemContent = (
          <Stack key={`menuItemStack-${key}`} onMouseEnter={() => handleMouseEnter(key)} onMouseLeave={handleMouseLeave}>
            <Box
              key={`menuItem-${key}`}
              sx={{
                cursor: isDisabledPage ? "not-allowed" : "pointer",
                background: activeMenu?.key === menuItem?.key ? globalTheme.activeMenu : undefined,
                color: activeMenu?.key === menuItem?.key ? globalTheme.white : undefined,
                paddingX: isLeftNavExpanded ? "20px" : "4px",
                paddingY: "8px",
                borderRadius: "4px",
                width: isLeftNavExpanded ? "200px" : "25px",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "left",
                transition: "all 0.2s",
                ":hover": {
                  color: activeMenu.key !== menuItem.key ? globalTheme.activeMenu : undefined,
                },
                opacity: isDisabledPage ? 0.5 : 1, // Opacity for disabled effect
              }}
              onClick={() => !isDisabledPage && handleLeftNavSelection(key)} // Prevent click event for disabled icons and Click is not is disabled for othericons
            >
              <Stack direction="row" spacing={1} alignItems="center" width="100%">
                {menuItem.icon &&
                  cloneElement(menuItem.icon as ReactElement, {
                    fill: activeMenu.key === menuItem.key ? globalTheme.white : globalTheme.primaryColor,
                    style: isDisabledPage ? { color: globalTheme.border } : {}, // Apply disabled color
                  })}
                {isLeftNavExpanded && (
                  <Box
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      width: "75%",
                    }}
                  >
                    {menuItem.title}
                  </Box>
                )}
              </Stack>
            </Box>
            {menuItem.submenu && isLeftNavExpanded && (
              <Collapse in={hoveredMenu === key || activeMenu.key === menuItem.key} timeout="auto" unmountOnExit>
                <Stack
                  spacing={0.5}
                  sx={{
                    p: 1,
                    marginTop: 1,
                    marginBottom: 2,
                  }}
                >
                  {menuItem.submenu.map((subItem, subKey) => {
                    const activeSubmenu = localStorage.getItem(menuItem.localStorageSetSubMenu);
                    const isSubmenuSelected = activeSubmenu === String(subItem.tabId);

                    return (
                      <Box
                        key={subKey}
                        sx={{
                          cursor: "pointer",
                          paddingY: "4px",
                          paddingX: "8px",
                          borderRadius: "4px 4px 4px 4px",
                          fontSize: "0.875rem",
                          paddingLeft: 5,
                          color:
                            activeMenu?.title === menuItem.title && isSubmenuSelected
                              ? globalTheme.white
                              : globalTheme.primaryColor,
                          backgroundColor:
                            activeMenu?.title === menuItem.title && isSubmenuSelected ? globalTheme.activeMenu : undefined,
                          ":hover": {
                            color:
                              activeMenu?.title !== menuItem.title && isSubmenuSelected ? globalTheme.white : globalTheme.white,
                            backgroundColor:
                              activeMenu?.title === menuItem.title && isSubmenuSelected
                                ? globalTheme.activeMenu
                                : globalTheme.activeMenu,
                          },
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => handleOnClickSubmenuItem(subItem, menuItem)}
                      >
                        {subItem.icon &&
                          cloneElement(subItem.icon as ReactElement, {
                            fill: isSubmenuSelected ? globalTheme.white : globalTheme.primaryColor,
                            fontSize: "small",
                          })}

                        <Typography variant="body2" sx={{ marginLeft: "8px" }}>
                          {subItem.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Collapse>
            )}
          </Stack>
        );

        return (
          <Stack key={`menuItemStack-${key}`}>
            {!isLeftNavExpanded ? (
              <Tooltip title={tooltipTitle} placement="right" arrow>
                {menuItemContent}
              </Tooltip>
            ) : (
              menuItemContent
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
